import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { combineLatest, pipe, switchMap } from 'rxjs';
import { selectFocusedLocation, selectValidationHistory, selectValidationHistoryLoading } from '../../location-list/state/location-list-selectors';
import { debounceTime, filter, map, tap } from 'rxjs/operators';
import { areCoordsValid, createLocationMarkers, CvMarkerType, MapMarkerData } from '../map-utils';
import { round } from '@turf/helpers';
import { VALIDATED_MARKER_ID } from '../../constants';
import { groupBy } from 'lodash';
import { CvMapSignalStore } from './cv-map-signal-store';
import { selectShowValidationHistory } from '../state/map-selectors';

export function withCvMarkers<_>() {
  return signalStoreFeature(
      { state: type<CvMapSignalStore>() },
      withMethods((signalStore, store = inject(Store)) => ({
        // Effect - needs to be triggered in onInit!
        // Generate markers that can overlap (autocomplete and custom markers are stored separately)
        generateMarkers: rxMethod<void>(pipe(
            switchMap(() => combineLatest([
              store.select(selectFocusedLocation),
              store.select(selectValidationHistoryLoading),
              store.select(selectValidationHistory),
              store.select(selectShowValidationHistory),
            ])),
            // Skip loop while loading
            filter(([, dataLoading]) => !dataLoading),
            debounceTime(50),
            map(([focusedLocation, , validationData, showHistory]) => {
              if (!focusedLocation) {
                return [];
              }

              const isVerified = focusedLocation.verified;
              const roundingPrecision = 5;

              // 1. Identify the "Source" marker that we want to merge into the Geocode pin
              const lastValidation = isVerified && validationData?.[0] ?
                  validationData[0] :
                  null;

              let flatMarkers: MapMarkerData[] = [];

              if (showHistory && validationData.length > 0) {

                flatMarkers = [
                  ...createLocationMarkers(focusedLocation),
                  ...validationData
                      .filter(h => areCoordsValid(h.coordinates))
                      // 2. If verified, filter out the specific history record
                      // that is going to be used to change the type (validation src) of the
                      // VALIDATED marker
                      .filter(h => !(isVerified && lastValidation && h.id === lastValidation.id))
                      .map((h) => ({
                        id: String(h.id),
                        type: CvMarkerType.HISTORY,
                        position: {
                          lat: round(h.coordinates.latitude, roundingPrecision),
                          lng: round(h.coordinates.longitude, roundingPrecision),
                        },
                      })),
                ];
              } else {
                flatMarkers = createLocationMarkers(focusedLocation);
              }

              // Update VALIDATED marker's type with validation src data if available
              const updatedFlatMarkers = flatMarkers.map(marker => {
                if (marker.id !== VALIDATED_MARKER_ID) return marker;

                return {
                  ...marker,
                  // override type if data available
                  type: (lastValidation?.source || marker.type) as CvMarkerType,
                };
              });

              // Group by coordinate string to handle overlapping
              const grouped = groupBy(updatedFlatMarkers, m =>
                  `${m.position.lat},${m.position.lng}`);

              return Object.values(grouped).map(group => {
                if (group.length > 1) {
                  return {
                    id: `${group[0].position.lat},${group[0].position.lng}`,
                    type: CvMarkerType.GROUP,
                    position: group[0].position,
                    overlappingMarkers: group,
                  } as MapMarkerData;
                }
                return group[0];
              });
            }),
            tap((processedMarkers: MapMarkerData[]) => {
              patchState(signalStore, { markers: processedMarkers });
            }),
        )),
      })),
  );
}